/**
 * Hero Home block script.
 *
 * Responsibilities:
 *   1. Page-load entrance choreography across [data-hero-step] elements,
 *      top to bottom with the project stagger. Above-the-fold so we sequence
 *      on DOMContentLoaded, not on scroll.
 *   2. Inline-autoplay background-video treatment for the hero. Honors
 *      prefers-reduced-motion (WCAG 2.3.3) and provides a visible pause/play
 *      toggle (WCAG 2.2.2). YouTube/Vimeo embeds are paused via postMessage.
 *   3. Initializes the shared video modal so the "play with sound"
 *      affordance can open the existing fullscreen modal player.
 */

import { entranceSequence } from '@/motion-primitives';
import { initVideoModal } from '@/video-modal';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Determine whether reduced motion is currently preferred.
 *
 * @returns {boolean}
 */
function prefersReducedMotion() {
	return !!(window.matchMedia && window.matchMedia(REDUCED_MOTION_QUERY).matches);
}

/**
 * Post a play/pause command into a YouTube or Vimeo iframe.
 *
 * @param {HTMLIFrameElement} iframe
 * @param {'play'|'pause'} action
 */
function postEmbedCommand(iframe, action) {
	if (!iframe || !iframe.contentWindow) return;

	const src = iframe.getAttribute('src') || '';
	try {
		if (src.includes('youtube')) {
			const command = action === 'play' ? 'playVideo' : 'pauseVideo';
			iframe.contentWindow.postMessage(
				JSON.stringify({ event: 'command', func: command, args: [] }),
				'*'
			);
		} else if (src.includes('vimeo')) {
			iframe.contentWindow.postMessage(
				JSON.stringify({ method: action }),
				'*'
			);
		}
	} catch (e) {
		// postMessage may fail if the iframe is cross-origin and not loaded yet;
		// safe to swallow — the embed will autoplay on its own.
	}
}

/**
 * Append a YouTube `enablejsapi=1` param so we can postMessage commands.
 *
 * @param {HTMLIFrameElement} iframe
 */
function enableYouTubeApi(iframe) {
	const src = iframe.getAttribute('src');
	if (!src || !src.includes('youtube')) return;
	if (src.includes('enablejsapi=1')) return;

	const separator = src.includes('?') ? '&' : '?';
	iframe.setAttribute('src', `${src}${separator}enablejsapi=1`);
}

/**
 * Set the visible state of the pause/play toggle.
 *
 * @param {HTMLButtonElement} button
 * @param {boolean} paused
 */
function syncPauseButton(button, paused) {
	if (!button) return;

	button.setAttribute('aria-pressed', paused ? 'true' : 'false');
	button.setAttribute(
		'aria-label',
		paused ? 'Play background video' : 'Pause background video'
	);

	const pauseIcon = button.querySelector('.rmh-hero-home__pause-icon');
	const playIcon = button.querySelector('.rmh-hero-home__play-icon');

	if (pauseIcon) {
		pauseIcon.classList.toggle('hidden', paused);
	}
	if (playIcon) {
		playIcon.classList.toggle('hidden', !paused);
	}
}

/**
 * Wire up an individual hero instance: bind the pause button, honor
 * reduced-motion, and forward play/pause to the appropriate <video> or iframe.
 *
 * @param {HTMLElement} hero
 */
function initHeroVideo(hero) {
	const video = hero.querySelector('[data-hero-video-upload]');
	const embed = hero.querySelector('[data-hero-video-embed]');
	const pauseBtn = hero.querySelector('[data-hero-video-pause]');

	if (!video && !embed) return;

	if (embed) {
		enableYouTubeApi(embed);
	}

	const setPaused = (paused) => {
		if (video) {
			if (paused) {
				video.pause();
			} else {
				const result = video.play();
				if (result && typeof result.catch === 'function') {
					result.catch(() => {
						// Autoplay was blocked; surface as paused so the user can manually play.
						syncPauseButton(pauseBtn, true);
					});
				}
			}
		}
		if (embed) {
			postEmbedCommand(embed, paused ? 'pause' : 'play');
		}
		syncPauseButton(pauseBtn, paused);
	};

	if (pauseBtn) {
		pauseBtn.addEventListener('click', () => {
			const currentlyPaused = pauseBtn.getAttribute('aria-pressed') === 'true';
			setPaused(!currentlyPaused);
		});
	}

	// Reduced-motion: start paused. Strip `autoplay` from upload <video> and
	// send a pause command to embed iframes so they honor the OS preference.
	if (prefersReducedMotion()) {
		if (video) {
			video.removeAttribute('autoplay');
			video.pause();
		}
		if (embed) {
			// Wait for the iframe to settle before sending the pause command.
			embed.addEventListener('load', () => postEmbedCommand(embed, 'pause'), { once: true });
		}
		syncPauseButton(pauseBtn, true);
	}

	// Keep the toggle in sync with native playback state for upload videos.
	if (video) {
		video.addEventListener('play', () => syncPauseButton(pauseBtn, false));
		video.addEventListener('pause', () => syncPauseButton(pauseBtn, true));
	}

	// Pause the background video while the fullscreen modal is open so the
	// two audio sources don't clash, then resume on close (unless reduced
	// motion was preferred or the user had manually paused it).
	let wasPausedBeforeModal = false;
	document.addEventListener('rmh:video-modal-open', () => {
		wasPausedBeforeModal = pauseBtn && pauseBtn.getAttribute('aria-pressed') === 'true';
		setPaused(true);
	});
	document.addEventListener('rmh:video-modal-close', () => {
		if (!wasPausedBeforeModal && !prefersReducedMotion()) {
			setPaused(false);
		}
	});
}

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('.rmh-hero-home').forEach((hero) => {
		const steps = hero.querySelectorAll('[data-hero-step]');
		entranceSequence(Array.from(steps));

		initHeroVideo(hero);
	});

	initVideoModal();
});
